import { DropdownSelect } from '@/components/ui/dropdown-select';
import { useAuthStore } from '@/features/auth/auth.store';
import { useOpenSnapshot } from '@/hooks/use-open-snapshot';
import { orderSelectedFirst } from '@/utils/order-selected-first';
import { UserAccessService } from '@/features/user-access/user-access.service';
import type { IBranch } from '@/features/user-access/user-access.types';
import { cn } from '@/lib/cn';
import { useQuery } from '@tanstack/react-query';
import { concat, isEmpty } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import type { Props } from 'react-select';

/**
 * One page big enough to hold every branch a company has, asked for up front.
 *
 * It used to be the *widened* page: the field mounted asking for 100 and only
 * grew to this once `selectAll` went on. But the tick is a mirror of the value,
 * so on any record that covers every branch it goes on just after hydration —
 * which changed the query key under a query that had already answered and
 * fetched the identical branch list a second time. A gift card or coupon edit
 * screen asked `/branches` twice on the way in, at 100 and then at 500.
 *
 * Asking for the whole list from the start costs the same one request — a
 * company with fewer branches than this sends the same rows either way — and
 * it is what "all locations" needs regardless: the value is stored as the ids
 * it stands for, so the field has to know all of them rather than the first
 * page the operator happened to scroll past.
 */
const BRANCH_PAGE_LIMIT = 500;

/**
 * Alphabetical by name, the way someone scanning the list reads it — the same
 * comparator the activity select uses, for the same reasons.
 *
 * `sensitivity: 'base'` so a lower-case branch ("kopos") files under K instead
 * of after Z, and so the accents in Dutch names do not split otherwise
 * adjacent entries. `numeric` so a branch named with a leading number sorts as
 * a number.
 *
 * Sorted here rather than asked of the API: the endpoint takes a `sort`
 * parameter but every caller sends it empty, and ordering the loaded options
 * is enough for a list of branches.
 */
const byName = (a: IBranch, b: IBranch) =>
  (a.name ?? '').localeCompare(b.name ?? '', undefined, {
    numeric: true,
    sensitivity: 'base',
  });

interface FormAsyncLocationSelectProps {
  name: string;
  label?: string;
  optional?: boolean;
  required?: boolean;
  optionKey?: string;
  formWrapperClassName?: string;
  /**
   * Ticked, the field fills itself with every branch. The ids are stored, not
   * a flag — the API has no "all" value, and an empty list reads as *no*
   * locations rather than every one.
   *
   * The field is not locked while it is on: a ticked list still has to be
   * narrowable, and pulling one branch off is how you say "all but that one".
   * The tick follows the value back down through `onAllSelectedChange`.
   *
   * Emptying it again on an untick is the caller's job, on the tick's own
   * `onValueChange` — see the gift card and coupon forms. It cannot live here:
   * this component only sees a boolean, and both forms mount before their
   * record arrives and then `reset()` into it, which flips the same boolean
   * off without anybody having touched the tick. Clearing on that would wipe a
   * stored card's locations the moment its edit screen finished loading.
   *
   * Multi-select only; on a single select there is nothing to select all of.
   */
  selectAll?: boolean;
  /**
   * Fires when "every branch there is" starts or stops being true of the
   * field's own value, so the tick can mirror it: a card saved covering all of
   * them opens with the box on, and pulling one branch back off turns it off
   * again without the operator having to find the box.
   *
   * Only the field can answer this — the branch list lives here, and a form
   * hydrating from a record has no way to tell "these nine" from "all nine".
   */
  onAllSelectedChange?: (isAllSelected: boolean) => void;
  onValueChange?: (branch: IBranch | IBranch[]) => void;
}

function FormAsyncLocationSelect<IsMulti extends boolean = false>({
  name,
  label,
  optional,
  required,
  optionKey,
  formWrapperClassName,
  selectAll = false,
  onAllSelectedChange,
  onValueChange,
  ...props
}: Props<IBranch, IsMulti> & FormAsyncLocationSelectProps) {
  const user = useAuthStore(state => state.user);
  const form = useFormContext();

  const [options, setOptions] = useState<IBranch[]>([]);

  // The selection the menu opened with, which is what its order follows.
  const pinned = useOpenSnapshot<unknown[] | null>(null);

  const [filters, setFilters] = useState({
    search: '',
    companyId: user?.company?.id ?? '',
    page: 1,
    limit: BRANCH_PAGE_LIMIT,
    sort: '',
    order: '',
    status: 'active',
  });

  const { data, isLoading } = useQuery({
    queryKey: ['locations', { ...filters }],
    queryFn: UserAccessService.getListLocation,
    refetchOnWindowFocus: false,
    gcTime: 0,
    staleTime: 0,
  });

  const handleLoadMore = () => {
    if (+(data?.meta?.page ?? 0) === data?.meta.total_pages) return;

    setFilters(prev => ({ ...prev, page: prev.page + 1 }));
  };

  useEffect(() => {
    if (data?.data) {
      setOptions(prev => {
        const existingIds = new Set(prev.map(item => item.id));
        const newItems = (data?.data ?? []).filter(
          item => !existingIds.has(item.id)
        );
        // Sorted over the whole list, not appended and left: a second page
        // settles in among the first rather than restarting the alphabet
        // halfway down the menu.
        return concat(prev, newItems).sort(byName);
      });
    }
  }, [data?.data]);

  useEffect(() => {
    return () => setOptions([]);
  }, []);

  const allOptionValues = useMemo(
    () => options.map(option => option[optionKey ?? 'id']),
    [options, optionKey]
  );

  /**
   * Whether the tick has moved since mount, which is the only thing that makes
   * the fill below a *change*. A form whose defaults arrive ticked — a new gift
   * card, say — is not dirty for having its own default in it, and marking it
   * so put the unsaved-changes guard in front of an untouched screen.
   */
  /**
   * `useFormContext()` hands back a **fresh object every render** — RHF's
   * `FormProvider` spreads its props into a new context value each time — so
   * `form` in an effect's dependency list means "run on every render". The fill
   * below must not: it would undo the operator's own removals, re-adding a
   * branch the moment they took it off. The form is reached through a ref so
   * the effect depends only on the things that should actually retrigger it.
   */
  const formRef = useRef(form);
  formRef.current = form;

  const previousSelectAll = useRef(selectAll);
  const wasToggled = useRef(false);

  /**
   * Whether the tick has already had its go at the field this time round.
   * Until it has, a field short of every branch is the fill still in flight —
   * not the operator having taken one off — and the two have to be told apart
   * before the report below can speak.
   */
  const hasFilled = useRef(false);

  useEffect(() => {
    if (previousSelectAll.current !== selectAll) {
      wasToggled.current = true;
      // A tick going back on fills from scratch: the untick emptied the field.
      if (selectAll) hasFilled.current = false;
    }

    previousSelectAll.current = selectAll;
  }, [selectAll]);

  const selectedValues = useWatch({ control: form.control, name }) as
    | unknown[]
    | undefined;

  /**
   * Whether the question is answerable yet: every page in, and at least one
   * branch to compare against. On a half-loaded list neither "all of them" nor
   * "not all of them" is a claim the field can make.
   */
  const canTellAllSelected =
    (data?.meta?.total_pages ?? 1) <= filters.page &&
    allOptionValues.length > 0;

  /**
   * Every branch there is — not merely every branch loaded so far — so a
   * half-loaded list never ticks a box the operator would have to undo.
   */
  const isAllSelected =
    canTellAllSelected &&
    allOptionValues.every(value => (selectedValues ?? []).includes(value));

  // Held in a ref so an inline arrow at the call site — which every caller
  // writes — cannot turn this into a render loop through the form state it
  // sets.
  const reportAllSelected = useRef(onAllSelectedChange);
  reportAllSelected.current = onAllSelectedChange;

  const wasAllSelected = useRef<boolean | null>(null);

  useEffect(() => {
    // Silence until the list can actually answer. The first render has no
    // options yet, so this used to report "not all" the moment it mounted —
    // which unticked the caller's own default before the branches it stands
    // for had arrived, and with the tick off the fill below never ran. A new
    // gift card opened redeemable *nowhere* instead of everywhere.
    if (!canTellAllSelected) return;

    // Same silence for the gap between the branches landing and the fill
    // putting them in the field: this effect is declared above that one and so
    // runs first, and answering out of that half-step reported an emptiness
    // the next commit was already filling.
    if (selectAll && !hasFilled.current) return;

    if (wasAllSelected.current === isAllSelected) return;

    wasAllSelected.current = isAllSelected;
    reportAllSelected.current?.(isAllSelected);
  }, [isAllSelected, canTellAllSelected, selectAll]);

  // Keeps the value level with the option list while the tick is on, which
  // matters on the second page as much as the first: a branch that loads after
  // the tick still belongs to "all locations".
  useEffect(() => {
    if (!selectAll || allOptionValues.length === 0) return;

    const current: unknown[] = formRef.current.getValues(name) ?? [];
    const isAlreadyAll =
      current.length === allOptionValues.length &&
      allOptionValues.every(value => current.includes(value));

    // The tick has had its go, whether or not it had anything to add — which
    // is what lets the report above start answering.
    hasFilled.current = true;

    if (isAlreadyAll) return;

    formRef.current.setValue(name, allOptionValues, {
      shouldDirty: wasToggled.current,
      shouldValidate: true,
    });
  }, [selectAll, allOptionValues, name]);

  return (
    <Controller
      name={name}
      control={form.control}
      render={({ field, fieldState }) => {
        const selectedOptions = options.filter(opt =>
          (field.value ?? []).includes(opt[optionKey ?? 'id'])
        );

        // A multi-select lists its ticked branches first, each half
        // alphabetical.
        const pinnedSelection: unknown[] = pinned.snapshot ?? field.value ?? [];
        const orderedOptions = props.isMulti
          ? orderSelectedFirst(
              options,
              opt => pinnedSelection.includes(opt[optionKey ?? 'id']),
              opt => opt.name ?? ''
            )
          : options;

        return (
          <div className={cn('flex flex-col gap-1.5', formWrapperClassName)}>
            {!isEmpty(label) && (
              <label
                htmlFor={name}
                className="text-text-secondary flex gap-1 text-sm"
              >
                {label}
                {optional && (
                  <span className="font-outfit text-text-muted text-xs">
                    (Optional)
                  </span>
                )}
                {required && <span className="text-feedback-error">*</span>}
              </label>
            )}
            <DropdownSelect<IBranch, IsMulti>
              isLoading={isLoading}
              options={orderedOptions}
              // A multi-select that closes on every pick reads as a single
              // one — you are returned to a shut field with one value in it,
              // with nothing to say another is allowed. Keeping the menu open
              // and the picked rows in place is what makes the second choice
              // discoverable.
              closeMenuOnSelect={!props.isMulti}
              hideSelectedOptions={!props.isMulti}
              // Picking locations is a list you tick through, so the menu says
              // so on every row instead of leaving the operator to infer it
              // from the first pick not closing the menu.
              withCheckboxes={Boolean(props.isMulti)}
              value={selectedOptions}
              getOptionValue={item => String(item[optionKey ?? 'id'])}
              getOptionLabel={item => item.name ?? ''}
              onChange={selectedOptions => {
                if (props.isMulti) {
                  const value = (selectedOptions as IBranch[])?.map(
                    option => option[optionKey ?? 'id']
                  );
                  field.onChange(value);
                  onValueChange?.(selectedOptions as IBranch[]);
                } else {
                  field.onChange(
                    (selectedOptions as IBranch)[optionKey ?? 'id']
                  );
                  onValueChange?.(selectedOptions as IBranch);
                }
              }}
              isError={fieldState.invalid}
              onMenuScrollToBottom={handleLoadMore}
              {...props}
              onMenuOpen={() => {
                pinned.capture(field.value ?? []);
                props.onMenuOpen?.();
              }}
              onMenuClose={() => {
                pinned.release();
                props.onMenuClose?.();
              }}
            />
            {fieldState.invalid && (
              <span data-field-error className="text-xs text-red-500">
                {fieldState.error?.message}
              </span>
            )}
          </div>
        );
      }}
    />
  );
}

export { FormAsyncLocationSelect };
